import { useState } from "react";
import { apiClient } from "../../lib/api";
import type { ImagePlacement } from "./ImagePlacementManager";

interface ImagePlacementCardProps {
  placement: ImagePlacement;
  imageUrl: string | undefined;
  onDelete: (id: number) => Promise<void>;
  onUpdate: () => Promise<void>;
  maxOrder: number;
  sectionPlacements: ImagePlacement[];
}

export function ImagePlacementCard({
  placement,
  imageUrl,
  onDelete,
  onUpdate,
  maxOrder,
  sectionPlacements,
}: ImagePlacementCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [sortOrder, setSortOrder] = useState(placement.sort_order);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editForm, setEditForm] = useState({
    link: placement.additional_config.link || "",
    isFeatureImage: placement.additional_config.isFeatureImage,
  });

  const handleOrderChange = async (newOrder: number) => {
    if (newOrder === placement.sort_order) {
      setIsEditing(false);
      return;
    }

    setIsUpdating(true);
    try {
      // 同じセクション内の他の画像の順序を調整
      const updates = sectionPlacements
        .map((p) => {
          if (p.id === placement.id) {
            // 対象の画像の順序を更新
            return {
              id: p.id,
              sort_order: newOrder,
            };
          } else if (
            // 上に移動する場合
            newOrder > placement.sort_order &&
            p.sort_order > placement.sort_order &&
            p.sort_order <= newOrder
          ) {
            return {
              id: p.id,
              sort_order: p.sort_order - 1,
            };
          } else if (
            // 下に移動する場合
            newOrder < placement.sort_order &&
            p.sort_order >= newOrder &&
            p.sort_order < placement.sort_order
          ) {
            return {
              id: p.id,
              sort_order: p.sort_order + 1,
            };
          }
          return null;
        })
        .filter(
          (update): update is { id: number; sort_order: number } =>
            update !== null
        );

      // 一括更新
      for (const update of updates) {
        await apiClient.patch(`/image_placements/${update.id}`, {
          sort_order: update.sort_order,
          additional_config: placement.additional_config // そのまま維持
        });
      }

      await onUpdate();
      setIsEditing(false);
    } catch (error) {
      console.error("順序の更新に失敗しました:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleEdit = async () => {
    setIsUpdating(true);
    try {
      await apiClient.patch(`/image_placements/${placement.id}`, {
        sort_order: placement.sort_order, // そのまま維持
        additional_config: {
          link: editForm.link || undefined,
          isFeatureImage: editForm.isFeatureImage,
        },
      });

      await onUpdate();
      setIsEditing(false);
    } catch (error) {
      console.error("設定の更新に失敗しました:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <div className="w-16 h-16 bg-gray-100 rounded overflow-hidden">
            {placement.illustration.storage_path && imageUrl && (
              <img
                src={imageUrl}
                alt={placement.illustration.title}
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div>
            <div className="font-medium">{placement.illustration.title}</div>
            <div className="text-sm text-gray-500 flex items-center space-x-2">
              <span>表示順序:</span>
              {isEditing ? (
                <select
                  value={sortOrder}
                  onChange={(e) => {
                    const newOrder = parseInt(e.target.value);
                    setSortOrder(newOrder);
                    handleOrderChange(newOrder);
                  }}
                  className="border rounded px-2 py-1 text-sm"
                  disabled={isUpdating}
                >
                  {Array.from({ length: maxOrder + 1 }, (_, i) => (
                    <option key={i} value={i}>
                      {i}
                    </option>
                  ))}
                </select>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-blue-600 hover:text-blue-700"
                >
                  {placement.sort_order}
                </button>
              )}
            </div>
            <div className="mt-1 text-sm text-gray-500">
              {placement.additional_config.link && (
                <div className="flex items-center gap-1">
                  <span>リンク:</span>
                  <a
                    href={placement.additional_config.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700 truncate max-w-[200px]"
                  >
                    {placement.additional_config.link}
                  </a>
                </div>
              )}
              {placement.additional_config.isFeatureImage && (
                <div className="flex items-center gap-1">
                  <span>サイズ:</span>
                  <span>大</span>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="px-3 py-1 text-sm text-blue-600 bg-blue-100 rounded hover:bg-blue-200"
            disabled={isUpdating}
          >
            {isEditing ? "キャンセル" : "編集"}
          </button>
          <button
            onClick={() => onDelete(placement.id)}
            className="px-3 py-1 text-sm text-red-600 bg-red-100 rounded hover:bg-red-200"
            disabled={isUpdating}
          >
            削除
          </button>
        </div>
      </div>

      {isEditing && (
        <div className="mt-4 space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              リンク
            </label>
            <input
              type="text"
              value={editForm.link}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  link: e.target.value,
                })
              }
              placeholder="https://example.com"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              サイズ大
            </label>
            <input
              type="checkbox"
              checked={editForm.isFeatureImage}
              onChange={(e) =>
                setEditForm({
                  ...editForm,
                  isFeatureImage: e.target.checked,
                })
              }
              className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleEdit}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={isUpdating}
            >
              {isUpdating ? "更新中..." : "更新"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
